import clsx from "clsx";
import * as React from "react";
import { Atoms, atoms, extractAtomsProps } from "../../core/atom";
import { icon } from "./icon.css";

export type IconProps = {
  size?: number;
  height?: number;
  width?: number;
  viewBox?: string;
  className?: string;
  isActionable?: boolean;
  colorScheme?: "primary";
  shiftBy?: number[];
} & React.SVGProps<SVGSVGElement> &
  Omit<Atoms, "height" | "width">;

const Icon = (props: IconProps & { children: React.ReactNode }) => {
  const {
    size = 24,
    height,
    width,
    shiftBy,
    viewBox = "0 0 24 24",
    colorScheme,
    isActionable,
    children,
    className,
    ...delegated
  } = props;
  const { atomProps, nativeProps } = extractAtomsProps(delegated);
  const classes = clsx([icon({ colorScheme: colorScheme }), atoms({ ...atomProps }), className]);
  return (
    <svg
      style={{
        pointerEvents: isActionable ? "auto" : "none",
        transform: shiftBy ? `translate(${shiftBy[0] ?? 0}px, ${shiftBy[1] ?? 0}px)` : "inherit",
      }}
      className={classes}
      width={width ?? size}
      height={height ?? size}
      aria-hidden={true}
      focusable={false}
      role={"presentation"}
      viewBox={viewBox}
      children={children}
      {...nativeProps}
    />
  );
};

export const LogoIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      d="M8.215 6.51a10.025 10.025 0 0 1 2.875-3.498 9.91 9.91 0 0 1 4.108-1.861l.009-.002c.103-.021.217-.042.347-.064l.009-.002.112-.018c.139-.022.288-.044.445-.065-.294.01-.587.032-.876.067a10.098 10.098 0 0 0-5.135 2.212 10.34 10.34 0 0 0-3.68 6.593l-.017.124c-.017.117-.034.24-.042.359l-.042.487-.042.45-.014.139a2.959 2.959 0 0 1-.04.321l-.072.441-.036.222c-.007.049-.019.097-.03.143a2.864 2.864 0 0 0-.019.08l-.102.439-.05.219-.067.22a17.171 17.171 0 0 1-1.335 3.328 15.438 15.438 0 0 1-2.052 2.975 16.82 16.82 0 0 1-1.598 1.59l-.037.031c-.262.238-.57.5-.834.717.12-.053.244-.11.363-.167l.004-.002h.004a.195.195 0 0 0 .02-.009l.015-.008.048-.023.048-.022c.03-.015.059-.03.088-.043a.26.26 0 0 0 .026-.014l.003-.001.004-.002.08-.039.003-.002.003-.001a.436.436 0 0 1 .06-.03 11.335 11.335 0 0 0 2.178-1.48 12.215 12.215 0 0 0 2.482-2.942l.25-.417.22-.436.11-.22.028-.056c.026-.051.05-.103.072-.157l.188-.456c.106-.242.186-.499.265-.747l.056-.174c.052-.152.095-.307.13-.463l.115-.48.054-.24.038-.235.078-.484a2.12 2.12 0 0 1 .013-.08c.01-.05.016-.1.02-.152l.025-.247.046-.487a3.26 3.26 0 0 0 .018-.324c0-.053.003-.105.005-.158l.016-.485.016-.454c.002-.105.011-.213.02-.317A9.785 9.785 0 0 1 8.215 6.51ZM19.662 7.922l-.01.002a6.744 6.744 0 0 0-1.416.6 6.692 6.692 0 0 0-1.991 1.75 6.746 6.746 0 0 0-1.163 2.39 5.697 5.697 0 0 1 .643.696 6.974 6.974 0 0 1 1.223-3.015 7.541 7.541 0 0 1 1.446-1.52c.286-.225.587-.431.9-.617l.12-.07.124-.068c.046-.027.091-.05.137-.074.097-.053.189-.1.273-.145-.093.02-.189.044-.286.07Zm-3.904 7.82-.012-.02-.056-.093a3.463 3.463 0 0 1-.101-.183l-.048-.09a2.997 2.997 0 0 1-.1-.2l-.16-.338a4.78 4.78 0 0 0-.408-.703c-.012.367.007.734.057 1.098l.077.561.007.085c.013.158.027.322.035.483a8.211 8.211 0 0 1-.237 2.254 7.871 7.871 0 0 1-.854 2.098c-.233.407-.501.792-.8 1.153l-.003.003-.001.002-.003.003c-.058.07-.155.186-.255.303 1.129-.794 2.03-2.027 2.49-3.421l.035-.114c.05-.154.1-.313.136-.473.02-.08.038-.16.056-.24l.014-.062.051-.302a7.665 7.665 0 0 0 .084-1.742l-.004-.062Z"
      fill="currentColor"
    />
    <path
      d="M22.11 18.368c-.753 0-1.5-.125-2.212-.37a6.63 6.63 0 0 1-3.518-2.804l-.054-.085a2.733 2.733 0 0 1-.089-.156l-.046-.083a2.362 2.362 0 0 1-.089-.172 3.16 3.16 0 0 0-.036-.075l-.128-.262a5.245 5.245 0 0 0-2.527-2.429 5.19 5.19 0 0 0-2.108-.454c-.058 0-.116 0-.174.003a5.02 5.02 0 0 0-.852.107 5.988 5.988 0 0 1 .85.009 5.543 5.543 0 0 1 2.133.64 5.361 5.361 0 0 1 1.664 1.416c.223.287.415.598.572.926l.12.254c.014.026.028.053.04.08.03.064.058.124.09.182.019.032.036.063.053.095.03.058.06.113.092.166l.054.087c.034.056.068.114.104.17a6.41 6.41 0 0 0 3.753 2.712 6.317 6.317 0 0 0 1.587.205 6.138 6.138 0 0 0 1.583-.212c-.276.033-.553.05-.83.05h-.033Z"
      fill="currentColor"
    />
  </Icon>
);

export const FacebookCircleIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      fill={"currentColor"}
      d="M12 .333C5.556.333.333 5.556.333 12S5.556 23.667 12 23.667l.25-.003v-7.692H9.666v-3.23h2.584v-1.938c0-2.66 1.502-4.522 3.875-4.522h2.584v3.23h-1.291c-1.291 0-1.938.646-1.938 1.938v1.292h3.23l-.646 3.23h-2.585v7.168c4.745-1.48 8.188-5.908 8.188-11.14C23.667 5.556 18.444.333 12 .333z"
    />
  </Icon>
);

export const InstagramCircleIcon = (props: IconProps) => (
  <Icon {...props}>
    <path fill={"currentColor"} d="M11.947 14.367a2.373 2.373 0 002.421-2.315 2.368 2.368 0 10-2.421 2.315z" />
    <path
      fill={"currentColor"}
      d="M15.61 12.434a3.62 3.62 0 01-1.569 2.575 3.632 3.632 0 01-3.81.163A3.55 3.55 0 018.82 13.75c-.494-.909-.573-1.863-.283-2.854H7.424v5.22c0 .249.215.463.464.463h8.228a.472.472 0 00.465-.465v-5.218h-1.115c.158.504.206 1.016.144 1.538z"
    />
    <path
      fill={"currentColor"}
      d="M14.558 8.843a.472.472 0 00.463.315h.533v-.002c.192 0 .385.003.577-.001a.469.469 0 00.45-.466V7.582a.472.472 0 00-.474-.476h-1.105a.473.473 0 00-.473.476c0 .366-.002.732 0 1.098.001.055.012.111.029.163z"
    />
    <path
      fill={"currentColor"}
      d="M12.001.333C5.558.333.334 5.556.334 12s5.224 11.667 11.667 11.667c6.444 0 11.668-5.223 11.668-11.667S18.445.333 12 .333zm5.844 16.344c-.009.052-.016.104-.026.156-.093.48-.483.878-.96.98-.06.013-.122.022-.183.032h-9.35l-.075-.012a1.265 1.265 0 01-1.06-.965c-.014-.06-.023-.12-.033-.178V7.313c.003-.022.008-.044.01-.066a1.26 1.26 0 01.976-1.06c.055-.012.112-.02.169-.03h9.377c.022.004.043.009.065.012.53.073.962.479 1.065 1.003.01.05.017.102.025.153v9.352z"
    />
  </Icon>
);

export const TwitterCircleIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      fill={"currentColor"}
      d="M12 .333C5.556.333.333 5.556.333 12S5.556 23.667 12 23.667 23.667 18.444 23.667 12 18.444.333 12 .333zm5.68 8.754c.005.126.008.252.008.38 0 3.872-2.947 8.337-8.338 8.337a8.292 8.292 0 01-4.492-1.317 5.914 5.914 0 004.339-1.213 2.934 2.934 0 01-2.738-2.035 2.904 2.904 0 001.324-.05 2.933 2.933 0 01-2.351-2.873v-.038c.395.22.848.352 1.328.367a2.929 2.929 0 01-.907-3.912 8.32 8.32 0 006.04 3.061 2.93 2.93 0 014.993-2.673 5.866 5.866 0 001.861-.71 2.936 2.936 0 01-1.288 1.62 5.831 5.831 0 001.683-.46 5.933 5.933 0 01-1.462 1.516z"
    />
  </Icon>
);

export const LinkedInCircleIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      fill={"currentColor"}
      d="M0 12C0 5.373 5.373 0 12 0s12 5.373 12 12-5.373 12-12 12S0 18.627 0 12zm8.48-2.061H5.76v8.17h2.72V9.94zm.18-2.528C8.641 6.611 8.068 6 7.137 6 6.208 6 5.6 6.61 5.6 7.411c0 .785.59 1.413 1.503 1.413h.018c.948 0 1.538-.628 1.538-1.413zm9.628 6.014c0-2.51-1.342-3.678-3.131-3.678-1.444 0-2.09.793-2.452 1.35V9.938h-2.72c.036.767 0 8.171 0 8.171h2.72v-4.563c0-.245.018-.488.09-.663.196-.488.644-.993 1.395-.993.985 0 1.378.75 1.378 1.847v4.372h2.72v-4.685z"
    />
  </Icon>
);

export const DownloadIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      d="M.75 14.85a.75.75 0 0 1 .75.75v3.75a1.5 1.5 0 0 0 1.5 1.5h18a1.5 1.5 0 0 0 1.5-1.5V15.6a.75.75 0 1 1 1.5 0v3.75a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V15.6a.75.75 0 0 1 .75-.75Z"
      fill={"currentColor"}
    />
    <path
      d="M11.469 17.781a.75.75 0 0 0 1.062 0l4.5-4.5a.75.75 0 0 0-1.062-1.062l-3.219 3.22V2.25a.75.75 0 1 0-1.5 0v13.19l-3.219-3.221a.751.751 0 0 0-1.062 1.062l4.5 4.5Z"
      fill={"currentColor"}
    />
  </Icon>
);

export const LinkedinIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      d="M1.12 8.764h4.507V22H1.119V8.764Zm4.654-3.68c0 1.136-.948 2.057-2.431 2.057-1.423 0-2.372-.92-2.342-2.057C.97 3.892 1.92 3 3.372 3c1.453 0 2.373.893 2.401 2.084M9.243 12.987c0-1.651-.059-3.059-.119-4.222h3.915l.207 1.813h.088c.593-.84 2.076-2.111 4.477-2.111 2.965 0 5.19 1.786 5.19 5.684V22h-4.507v-7.335c0-1.705-.652-2.87-2.284-2.87-1.244 0-1.986.786-2.282 1.544-.119.243-.178.65-.178 1.028V22H9.243v-9.013Z"
      fill="currentColor"
    />
  </Icon>
);

export const EmailIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      d="M22.995 5.78 14.322 12l8.665 6.215c.009-.058.013-.117.013-.176V5.876c0-.033-.003-.064-.005-.096ZM1.005 5.78c0 .033-.005.065-.005.097V18.04c0 .059.005.117.013.175L9.68 12 1.005 5.78ZM12 13.665l-1.463-1.05L1.634 19h20.732l-8.902-6.385L12 13.665ZM12 12.434 22.366 5H1.634L12 12.434Z"
      fill="currentColor"
    />
  </Icon>
);

export const InstagramIcon = (props: IconProps) => (
  <Icon {...props}>
    <path
      d="M7.586 3.62a3.97 3.97 0 0 0-3.967 3.964v8.831a3.97 3.97 0 0 0 3.967 3.966h8.829a3.97 3.97 0 0 0 3.966-3.966v-8.83a3.97 3.97 0 0 0-3.966-3.966h-8.83ZM16.415 22h-8.83A5.592 5.592 0 0 1 2 16.415v-8.83A5.592 5.592 0 0 1 7.586 2h8.829A5.591 5.591 0 0 1 22 7.584v8.831A5.592 5.592 0 0 1 16.415 22Z"
      fill={"currentColor"}
    />
    <path
      d="M12 8.357A3.65 3.65 0 0 0 8.356 12a3.648 3.648 0 0 0 3.646 3.643A3.648 3.648 0 0 0 15.647 12 3.649 3.649 0 0 0 12 8.357Zm0 8.905A5.268 5.268 0 0 1 6.739 12a5.269 5.269 0 0 1 5.263-5.262A5.269 5.269 0 0 1 17.264 12 5.268 5.268 0 0 1 12 17.262ZM17.485 7.338a.785.785 0 0 1-.437-.149.815.815 0 0 1-.284-.37.836.836 0 0 1 .187-.884.78.78 0 0 1 .866-.167.802.802 0 0 1 .354.3c.087.135.133.292.133.453a.84.84 0 0 1-.245.581.8.8 0 0 1-.574.236Z"
      fill={"currentColor"}
    />
  </Icon>
);
